import * as React from 'react';

import { Box, Tooltip, Typography } from '@mui/material';

import {
  FAMILY_LABEL,
  type TokenFamily,
  type TokenSummary,
} from './tokenProfile';

/**
 * What this norm set tastes like, at a glance.
 *
 * Three families, each a handful of tokens ordered by how much of the set
 * carries them. This is the question a norm set exists to answer — mean
 * distance is how the ranking was made, not what was ranked, so it sits in a
 * caption below rather than here.
 *
 * Bar length is coverage, on the same 0–100% scale in every row, so families
 * are comparable to each other and to the same component on the saved norm set.
 */

type Props = {
  families: Array<{ family: TokenFamily; tokens: TokenSummary[] }>;
  /** denominator behind every bar */
  total: number;
  /** false on a saved norm set, where the anchors' own tokens are not loaded */
  markNovel?: boolean;
};

const BAR_WIDTH = 132;

const TokenBar: React.FC<{
  summary: TokenSummary;
  total: number;
  markNovel: boolean;
}> = ({ summary, total, markNovel }) => {
  const pct = Math.round(summary.coverage * 100);
  const novel = markNovel && summary.novel;

  return (
    <Tooltip
      arrow
      enterDelay={300}
      title={`${summary.token} — ${summary.members} of ${total} products (${pct}%)${
        novel ? ', not in your anchors' : ''
      }`}
    >
      <Box sx={{ width: BAR_WIDTH }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 0.25,
              minWidth: 0,
            }}
          >
            {/* The token stands alone in its own node. Folding the marker into
                this one would make the label read "roasted ·" to anything
                matching on text, screen readers included. */}
            <Typography
              variant="caption"
              noWrap
              sx={{ color: novel ? 'text.secondary' : 'text.primary' }}
            >
              {summary.token.replace(/_/g, ' ')}
            </Typography>
            {novel && (
              <Typography
                aria-hidden
                variant="caption"
                sx={{ color: 'text.secondary' }}
              >
                ·
              </Typography>
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
          >
            {summary.members}
          </Typography>
        </Box>
        <Box
          sx={{
            height: 4,
            borderRadius: 2,
            backgroundColor: 'rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${pct}%`,
              height: '100%',
              backgroundColor: novel
                ? 'rgba(58,124,165,0.35)'
                : 'rgba(58,124,165,0.85)',
            }}
          />
        </Box>
      </Box>
    </Tooltip>
  );
};

const SensorySignature: React.FC<Props> = ({
  families,
  total,
  markNovel = true,
}) => {
  const anyNovel =
    markNovel && families.some((f) => f.tokens.some((t) => t.novel));

  return (
    <Box>
      {families.map(({ family, tokens }) => (
        <Box
          key={family}
          sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1 }}
        >
          <Typography
            variant="caption"
            sx={{
              width: 72,
              flexShrink: 0,
              pt: 0.25,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {FAMILY_LABEL[family]}
          </Typography>

          {/* An absent row would read as "no sensations". It means the export
              recorded none, which is a different claim. */}
          {tokens.length === 0 ? (
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              nothing recorded for these products
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {tokens.map((t) => (
                <TokenBar
                  key={t.token}
                  summary={t}
                  total={total}
                  markNovel={markNovel}
                />
              ))}
            </Box>
          )}
        </Box>
      ))}

      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
      >
        How many of the {total} products carry each token
        {anyNovel && ' · a dot marks one your anchors do not have'}. Only tokens
        common enough to be exported are counted, so a faint one may be missing
        rather than absent.
      </Typography>
    </Box>
  );
};

export default SensorySignature;
